import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Subject, Subscription } from 'rxjs';
import { TaskActionComponent } from '@Work/Accounts/proprietary/controls/task-controls/task-action/task-action.component';
import { RequiredEventDialogComponent } from '@Work/Accounts/proprietary/dialogs/required-event-dialog/required-event-dialog.component';
import { DynamicContentModalComponent } from '@Work/Accounts/proprietary/dynamic-content-outlet/dynamic-content-modal.component';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataSourceStatus } from '@Core/services/proprietary/datasource';
import { ObjectRuleService } from '@Core/services/proprietary/object-rule/object-rule.service';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { TaskService } from '@Core/services/proprietary/task.service';
import { cloneDeep } from 'src/ep-utils';
import { PageObjectService } from '../page/page-object.service';
import { EventType as PageEventType, PageState } from '../page/page.state';
import { ProjectObjectService } from '../project/project-object.service';
import {
    AddTaskToQueue,
    CloneTask,
    NewTask,
    NotifyDocumentSaved,
    PrepareRequest,
    RemoveTaskFromQueue,
    ResetState,
    ResetTask,
    SetCurrentReference,
    SetCurrentReferenceContract,
    SetCurrentTaskHeaderLayout,
    SetProjectState,
    SetTaskObjectState,
    SetTaskReference,
    SuppressTaskHeader,
    UpdateProject,
    UpdateTask,
    UpdateTaskHeader
} from './task-object.actions';
import { denormalize, normalize, rootSchema } from './task-object.normalize';
import { TaskObjectState } from './task-object.state';
import { EventType, TaskActionType, TaskHeaderLayoutType, TaskHeaderType } from './task-object.types';

@Injectable()
export class TaskObjectService implements OnDestroy {
    private dsTaskGetSub: Subscription;
    private dsTaskProcessSub: Subscription;
    private dsProjectProcessStatusSub: Subscription;
    private dsObjectTasksSub: Subscription;
    private autoSaveSub: Subscription;

    private taskID: any = null;
    private projectID: any = null;
    private autoNavigateOnComplete: boolean = false;
    private pageID: any = null;
    private rootRoute: string; // root page to return to on exit project
    private objectReloadCallback: any = null;
    private cloneInProgress: boolean = false;
    private selectExistingTaskInProgress: boolean = false;
    modalRef: NgbModalRef;

    //HACK! move this to state?  Use by project tracker
    public currentTrackerView = null;

    public selectExistingTaskTracker = new Subject();

    constructor(
        private store: Store,
        private app: AppService,
        private router: Router,
        private location: Location,
        private pageOS: PageObjectService,
        private taskService: TaskService,
        private projectOS: ProjectObjectService,
        private objectRuleService: ObjectRuleService,
        private os: ObjectService,
        private modalService: NgbModal
    ) { }

    get isProjectActive(): boolean {
        return this.projectID != null;
    }

    get currentProjectID(): any {
        return this.projectID;
    }

    ngOnDestroy() {
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
        if (this.dsTaskProcessSub) {
            this.dsTaskProcessSub.unsubscribe();
        }
        if (this.dsProjectProcessStatusSub) {
            this.dsProjectProcessStatusSub.unsubscribe();
        }
        if (this.dsObjectTasksSub) {
            this.dsObjectTasksSub.unsubscribe();
        }
        if (this.autoSaveSub) {
            this.autoSaveSub.unsubscribe();
        }
    }

    getPageURL(): string {
        let pageURL = '';
        if (this.pageID && this.pageID != '') {
            pageURL = this.app.pageParams.getPageURL(this.pageID);
        }
        return pageURL;
    }

    openObjectFromTask(task: any, forceTaskRetrieve: boolean = false, passCurrentInfo: boolean = false) {
        // console.log(`openObjectFromTask: ${JSON.stringify(task)}`);
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
        if (forceTaskRetrieve) {
            //console.log(`openObjectFromTask (retrieve task)  Task=${JSON.stringify(task)}`);

            // force retrive of task - use this optional parameter when you only have a partial task object
            if (!task && !task.Task_ID) {
                throw 'Task_ID must be provided to openObject function when forceTaskRetrive is true';
            }
            let loadTaskID = task.Task_ID;
            this.app.dsTaskGet.setStatus(DataSourceStatus.Initialized);
            this.app.dsTaskGet.get(
                {
                    json: 'CT2',
                    Task_ID: loadTaskID,
                    View: 'Full',
                    Assignment_Options: '[ALL]'
                },
                true,
                false
            );
            this.dsTaskGetSub = this.app.dsTaskGet.status$.subscribe((x) => {
                this.openObjectFromTaskGetStatusUpdated(x);
            });
            return;
        }

        //console.log(`openObjectFromTask (passed in task)  Task=${JSON.stringify(task)}`);

        if (!task && !task.Task_Object && !task.Task_Object.Object_Code) {
            throw 'ObjectCode must be provided to openObject function';
        }

        this.setCurrentReference(task);
        let objectCode = task.Task_Object.Object_Code.toLowerCase();
        let objectID = task.Task_Object.Object_ID;
        let taskID = task.Task_ID;
        let objectIDName = 'Object_ID';

        let route = '';
        let params: any = { Object_Code: objectCode };

        //Add Ref Entity's Role_Type to parms for Entity Note
        if (objectCode === 'noteent') {
            if (task.Task_Reference && task.Task_Reference.Entity && task.Task_Reference.Entity.Role_Type) {
                params.Role_Type = task.Task_Reference.Entity.Role_Type.toUpperCase();
            }
        }

        if (objectCode == 'contprint') {
            if (task.Task_Reference && task.Task_Reference.Contract && task.Task_Reference.Contract.Contract_ID) {
                params.Contract_ID = task.Task_Reference.Contract.Contract_ID;
            }
        }

        let objectConfig: any = this.os.getObjectConfig(objectCode);

        if (objectConfig && objectConfig.G2_Ind == 'Y') {
            route = objectConfig.G2_Route;
            objectIDName = objectConfig.Object_ID_Name;
        } else {
            route = 'not-supported';
        }

        if (objectID && objectID > 0) {
            params[objectIDName] = objectID;
        }
        if (taskID && taskID > 0) {
            params.Task_ID = taskID;
        }

        //If you have an Task_ID w/out an Object_ID - Use Pass_Current_Info parm so current info gets added to Page Params.
        if ((!(objectID && objectID > 0) && taskID && taskID > 0) || passCurrentInfo) {
            params.Pass_Current_Info = 'Y';
        }

        // Prevent Open of Deleted Object
        let deletedObject = false;
        if (task.Events && task.Events.Event) {
            task.Events.Event.forEach((element) => {
                if (element.Event_Code.toLowerCase() == 'delete') {
                    deletedObject = true;
                }
            });
        }
        if (deletedObject) {
            route = 'deleted-object';
        }

        this.router.navigate([route, params]);
    }

    openNextTaskInQueue() {
        let task = this.store.selectSnapshot(TaskObjectState.firstTaskInQueue);
        if (task) {
            this.openObjectFromTask(task, true);
            this.removeTaskFromQueue(task.Task_ID);
        } else {
            this.openProjectTracker();
        }
    }

    openObjectFromTaskGetStatusUpdated(status: DataSourceStatus) {
        console.log(`openOjectFromTaskGetStatusUpdated. Status=${status}`);
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsTaskGetSub.unsubscribe();
                this.openObjectFromTask(this.app.dsTaskGet.objectNode, false); //re-call openObjectFromTask
                break;
            case DataSourceStatus.Error:
                this.dsTaskGetSub.unsubscribe();
                break;
        }
    }

    async finishLater() {
        if (await this.autoSaveTask()) {
            this.loadTask(this.taskID, true, () => {
                this.openProjectTracker();
            });
        }
    }

    async createandroute() {
        if (await this.autoSaveTask()) {
            this.loadTask(this.taskID, true, () => {
                this.openRouteTaskAction();
            });
        }
    }

    openRouteTaskAction() {
        const modalRef = this.modalService.open(TaskActionComponent, { centered: true, backdrop: 'static' });
        modalRef.componentInstance.taskActionMode = TaskActionType.Route;

        modalRef.result.then(
            (result) => {
                this.openProjectTracker();
            },
            (reason) => { }
        );
    }

    openProjectTracker(task?: any) {
        //if task provided then load task.   if not, use current task
        if (task) {
            if (task.Task_ID && task.Task_ID != this.taskID) {
                this.loadTask(task.Task_ID);
            }
        }
        this.router.navigate(['project-tracker', { Project_ID: this.projectID, Selected_Task_ID: this.taskID }]);
    }

    openProjectTrackerByProjectID(projectID?: any) {
        if (projectID) {
            this.resetState();
            this.suppressTaskHeader(true);
            this.projectID = projectID;
            this.router.navigate(['project-tracker', { Project_ID: projectID }]);
        }
    }

    setRootRoute(route: string) {
        this.rootRoute = route;
    }

    async exitProject(autoNavigate: boolean = true) {
        if (autoNavigate) {
            if (!this.rootRoute || this.rootRoute === '') {
                this.rootRoute = '/my-work';
            }

            let unsavedChanges = this.store.selectSnapshot(PageState.unsavedChanges);
            if (unsavedChanges) {
                if (await this.router.navigateByUrl(this.rootRoute)) {
                    this.projectID = null;
                    this.suppressTaskHeader(true);
                    this.resetState();
                } else {
                    // Don't clear or reset project info if routing isn't successful.
                    // Router Guard that checks for dirty changes resolves false when user selects to stays on page.
                    console.log('Routing canceled by user.');
                }
            } else {
                this.router.navigateByUrl(this.rootRoute);
                this.projectID = null;
                this.suppressTaskHeader(true);
                this.resetState();
            }
        } else {
            this.projectID = null;
            this.suppressTaskHeader(true);
            this.resetState();
        }
    }

    openTaskDocuments() {
        this.router.navigate(['document-search', { Task_ID: this.taskID, Filed_Ind: 'Y', Doc_ID: 'FIRST' }]);
    }

    closeProject() {
        let body = {
            ConceptOneRequest: {
                SDK_Version: this.app.dsSystemInfo.data.ConceptOneReply.SDK_Version,
                ProjectProcess: {
                    Event: 'Close',
                    Project: {
                        Project_ID: this.projectID
                    }
                }
            }
        };
        //console.log(body)

        this.dsProjectProcessStatusSub = this.app.dsProjectProcess.status$.subscribe((x) => {
            if (x == DataSourceStatus.Ready) {
                this.projectCloseSuccessful();
            }
            if (x == DataSourceStatus.Error) {
                this.projectCloseFailed();
            }
        });
        this.app.dsProjectProcess.process({}, body);
    }

    private projectCloseSuccessful() {
        if (this.dsProjectProcessStatusSub) {
            this.dsProjectProcessStatusSub.unsubscribe();
        }
        this.resetState();
        this.router.navigate(['my-work', {}]);
    }

    private projectCloseFailed() {
        if (this.dsProjectProcessStatusSub) {
            this.dsProjectProcessStatusSub.unsubscribe();
        }
    }

    loadTask(taskID: any, autoNavigateOnComplete: boolean = false, objectReloadCallback: any = null) {
        this.objectReloadCallback = objectReloadCallback; //optional callback function to be executed after task is loaded if task is still open
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
        this.app.dsTaskProcess.reset(); //clear process datastore (resets error messages)

        if (this.taskID != taskID) {
            this.resetTask();
        }

        this.taskID = taskID;
        if (taskID) {
            this.autoNavigateOnComplete = autoNavigateOnComplete;
            this.app.dsTaskGet.get({
                json: 'CT2',
                Task_ID: taskID,
                View: 'Full',
                Assignment_Options: '[ALL]'
            });
            this.dsTaskGetSub = this.app.dsTaskGet.status$.subscribe((x) => {
                this.taskGetStatusUpdated(x);
            });
        } else {
            this.newTask();
            this.selectExistingTaskInProgress = false;
        }
    }

    loadTaskHeader(taskID: any, objectCode: any, objectID: any, pageID: string) {
        this.app.dsObjectTasks.reset();
        this.setPageID(pageID); //Store off the PageID, so if an existing Task is selected we can add the Task_ID to the PageParms.

        if (!taskID && objectID && 'notecont|noteent|noteclm'.includes(objectCode.toLowerCase())) {
            taskID = objectID;
        }

        if (taskID) {
            //existing task
            this.loadTask(taskID);
            this.suppressTaskHeader(false);
        } else {
            //adhoc task open from menu
            this.taskID = null;
            this.selectExistingTaskInProgress = false;
            if (
                objectCode &&
                objectCode.toLowerCase() != 'contractdb' &&
                objectCode.toLowerCase() != 'consumerdb' &&
                objectCode.toLowerCase() != 'brokerdb' &&
                objectCode.toLowerCase() != 'supplierdb' &&
                objectCode.toLowerCase() != 'acctinfo' &&
                objectCode.toLowerCase() != 'quickfind' &&
                objectCode.toLowerCase() != 'searchdoc' &&
                objectCode.toLowerCase() != 'searchcont' &&
                objectCode.toLowerCase() != 'searchent' &&
                objectCode.toLowerCase() != 'bookstats' &&
                objectCode.toLowerCase() != 'notereader' &&
                objectCode.toLowerCase() != 'smgaconfig' &&
                objectCode.toLowerCase() != 'service' &&
                objectCode.toLowerCase() != 'udproc'
            ) {
                //TODO: Better way of listing Objects in the dictionary that are non-taskable.
                //if from open object then set task header to that object type
                let objectDesc = '<New Task>';
                let objectConfig: any = this.os.getObjectConfig(objectCode);

                if (objectConfig && objectConfig.Object_Name) {
                    objectDesc = objectConfig.Object_Name;
                }
                let taskDefaults = { Task_Desc: objectDesc, Task_Object: { Object_Code: objectCode } };
                this.newTask(taskDefaults);
                this.suppressTaskHeader(false);

                // If Opened with an Object_Code and Object_ID, but no Task_ID, check for Open Tasks for the object.
                if (objectCode && objectID) {
                    if (['contract-market-set', 'contract-quote-set'].includes(this.getPageURL())) {
                        //Skip checking for open Tasks. Built into MarketSetGet and QuoteSetGet
                    } else {
                        this.getObjectTasks(objectCode, objectID);
                    }
                }
            } else {
                //otherwise treat as a non-taskable page
                this.taskID = null;
                this.resetTask();
                this.suppressTaskHeader(this.projectID == null); //suppress header if not in a project

                if (objectCode && objectCode.toLowerCase() == 'service') {
                    this.pageOS.setEffectiveRights(this.taskID, objectCode);
                }
            }
        }
    }

    getObjectTasks(objectCode: any, objectID: any) {
        if (this.dsObjectTasksSub) {
            this.dsObjectTasksSub.unsubscribe();
        }

        if (objectID && objectID > 0 && objectCode && objectCode > '') {
            this.app.dsObjectTasks.reset();

            this.app.dsObjectTasks.getCollection({
                View: 'Standard',
                Object_Code: objectCode,
                Object_ID: objectID,
                Task_Status: 'Open'
            });
        }

        this.dsObjectTasksSub = this.app.dsObjectTasks.status$.subscribe((status) => {
            switch (status) {
                case DataSourceStatus.Ready:
                    if (this.dsObjectTasksSub) {
                        this.dsObjectTasksSub.unsubscribe();
                    }

                    if (this.app.dsObjectTasks.objectNodes && this.app.dsObjectTasks.objectNodes.length > 0) {
                        this.selectExistingTaskDialog(true);
                    }

                    break;

                case DataSourceStatus.Error:
                    if (this.dsObjectTasksSub) {
                        this.dsObjectTasksSub.unsubscribe();
                    }
                    break;
            }
        });
    }

    selectExistingTaskDialog(showNewButton: boolean) {
        let component: string = 'SelectExistingTaskComponent';
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = {
            modal: true,
            showNewButton: showNewButton
        };
    }

    closeSelectExistingTaskDialog(selected: boolean, task?: any) {
        if (this.modalRef) {
            this.modalRef.close();
        }

        switch (selected) {
            case true: //Selected Existing Task
                this.selectExistingTaskInProgress = true;
                this.app.dsObjectTasks.reset();
                this.app.pageParams.setParameter(this.pageID, 'Task_ID', task.Task_ID);
                this.pageOS.setPristine();
                this.pageOS.setReadOnlyMode(false);
                this.pageOS.setProtectedMode(false, true);
                this.loadTaskHeader(task.Task_ID, task.Task_Object.Object_Code, task.Task_Object.Object_ID, this.pageID);
                break;

            case false: //Selected New or Cancel.
                break;
        }
    }

    newTask(taskOptions?: any) {
        this.taskID = '-1'; //Use same value that NewTask action sets. Process method has logic that needs this.taskID to be set.
        let objectCode = null;
        if (taskOptions && taskOptions.Task_Object && taskOptions.Task_Object.Object_Code) {
            objectCode = taskOptions.Task_Object.Object_Code;
        }
        this.pageOS.setPristine();
        this.pageOS.setEffectiveRights(this.taskID, objectCode);
        this.store.dispatch(new NewTask(taskOptions));
    }

    process(event: EventType, tranOptions: any = {}, validate: boolean = false) {
        if (validate) {
            if (!this.validate(event)) {
                return;
            }
        }

        let terminiatingEvents = [
            EventType.Complete,
            EventType.Delete,
            EventType.Skip,
            EventType.Start,
            EventType.Option1,
            EventType.Option2,
            EventType.Option3,
            EventType.Option4,
            EventType.Option5,
            EventType.Option6,
            EventType.Option7
        ];

        let keepTaskOpen = this.pageOS.keepTaskOpen(event, terminiatingEvents, true);

        if (event == EventType.Complete && keepTaskOpen == 'Y') {
            this.modalService.open(RequiredEventDialogComponent, {
                centered: true,
                size: 'lg'
            });
            return;
        }

        this.store.dispatch(
            new PrepareRequest({
                Keep_Task_Open: keepTaskOpen,
                Event: event,
                ...tranOptions
            })
        );

        let stateEntities = this.store.selectSnapshot(TaskObjectState.entities);

        stateEntities = cloneDeep(stateEntities);

        if (stateEntities.Task && stateEntities.Task['-1']) {
            stateEntities.Transaction[1].Event = event.replace(EventType.Update, EventType.Create);

            if (stateEntities.Transaction[1].Event == EventType.Delete) {
                this.location.back();
                return;
            }
        }

        // Remove Route Node if neither an assigned to user or workgroup
        let removeRouteNode = false;
        if (stateEntities.Task[this.taskID] && stateEntities.Task[this.taskID].Route) {
            if (!stateEntities.Task[this.taskID].Route.Assign_To) {
                removeRouteNode = true;
            } else {
                if (
                    stateEntities.Task[this.taskID].Route.Assign_To.User_Entity_ID == '' &&
                    stateEntities.Task[this.taskID].Route.Assign_To.WorkGroup_ID == ''
                ) {
                    removeRouteNode = true;
                }
            }
            if (removeRouteNode) {
                delete stateEntities.Task[this.taskID].Route;
            }
        }

        let tran = denormalize('1', rootSchema, stateEntities);

        this.app.dsTaskProcess.process({ json: 'CT2' }, tran);
        this.dsTaskProcessSub = this.app.dsTaskProcess.status$.subscribe((x) => {
            this.taskProcessStatusUpdated(x);
        });
    }

    taskProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsTaskProcessSub.unsubscribe();
                if (!this.cloneInProgress) {
                    if (!this.taskID || this.taskID == '0' || this.taskID == '-1') {
                        this.taskID = this.app.dsTaskProcess.tranNode.Task_ID;
                        this.app.pageParams.setParameter(this.pageID, 'Task_ID', this.taskID);
                    }
                    if (this.app.dsTaskProcess.tranNode.Event == 'Delete') {
                        if (this.app.dsTaskProcess.tranNode.Task_ID == this.app.dsTaskProcess.tranNode.Session_ID) {
                            this.exitProject();
                        } else {
                            this.location.back();
                        }
                    } else {
                        //re-retrive   TODO: is there a better way to refresh data?   Should process automatically do get?
                        this.loadTask(this.taskID, true);
                    }
                } else {
                    this.cloneInProgress = false;
                    this.taskID = this.app.dsTaskProcess.tranNode.Task_ID;
                    let task = { Task_ID: this.taskID };
                    this.openObjectFromTask(task, true);
                }

                break;
            case DataSourceStatus.Error:
                this.dsTaskProcessSub.unsubscribe();
                this.cloneInProgress = false;
                break;
        }
    }

    taskGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsTaskGetSub.unsubscribe();
                this.pageOS.setPristine();
                this.load(this.app.dsTaskGet.data); //reload task info into state

                if (
                    this.app.dsTaskGet.objectNode &&
                    this.app.dsTaskGet.objectNode.Task_Object &&
                    this.app.dsTaskGet.objectNode.Task_Object.Object_Code &&
                    this.app.dsTaskGet.objectNode.Task_Object.Object_Code.toLowerCase() == 'task'
                ) {
                    this.setValidEvents(); //Only set valid events if the Task is for a Manual Task
                }
                if (!this.autoNavigateOnComplete || (this.autoNavigateOnComplete && !this.autoNavigateToNextTask())) {
                    let objectCode: string = null;
                    let myTask: boolean = true;
                    if (
                        this.app.dsTaskGet.objectNode &&
                        this.app.dsTaskGet.objectNode.Task_Object &&
                        this.app.dsTaskGet.objectNode.Task_Object.Object_Code
                    ) {
                        objectCode = this.app.dsTaskGet.objectNode.Task_Object.Object_Code;
                    }
                    let taskResolved = this.app.dsTaskGet.objectNode.Resolved_Date > '';
                    if (
                        this.app.dsTaskGet.objectNode.Assigned_To &&
                        this.app.dsTaskGet.objectNode.Assigned_To.Entity_ID &&
                        this.app.dsTaskGet.objectNode.Assigned_To.Entity_ID > 0
                    ) {
                        myTask = this.app.dsTaskGet.objectNode.Assigned_To.Entity_ID == this.app.UserID;
                    }
                    let wfStep = this.app.dsTaskGet.objectNode.Workflow_Step;
                    if (wfStep?.View?.View_ID) {
                        this.app.pageParams.setParameter(this.pageID, 'Criteria_View_ID', wfStep?.View?.View_ID);
                    }
                    this.pageOS.setEffectiveRights(
                        this.app.dsTaskGet.objectNode.Task_ID,
                        objectCode,
                        wfStep,
                        taskResolved,
                        myTask
                    );
                    if (this.objectReloadCallback) {
                        this.objectReloadCallback(); // reload object into state (optional callback to object service function to load the object)
                    }
                    let missingsEvents = this.getMissingEvents();
                    if (missingsEvents.length > 0) {
                        this.modalService.open(RequiredEventDialogComponent, {
                            centered: true,
                            size: 'lg'
                        });
                    }
                }
                this.objectReloadCallback = null;
                if (this.selectExistingTaskInProgress === true) {
                    this.selectExistingTaskTracker.next(null);
                    this.selectExistingTaskInProgress = false;
                }
                break;
            case DataSourceStatus.Error:
                this.dsTaskGetSub.unsubscribe();
                this.objectReloadCallback = null;
                this.selectExistingTaskInProgress = false;
                break;
        }
    }

    autoNavigateToNextTask(): boolean {
        //for efficiency, this check is done before loading the task into state
        if (this.app.dsTaskGet.objectNode.Resolved_Date) {
            if (this.app.dsTaskGet.objectNode.Next_Tasks) {
                let nextStepFound = false;
                let autoCompletedBranchTaskID = null;
                let nextTask = this.app.dsTaskGet.objectNode.Next_Tasks.Task || [];
                let i = 0;
                for (i = 0; i < nextTask.length; i++) {
                    if (nextTask[i].Task_Status == 'Done') {
                        if (nextTask[i].Task_Object && nextTask[i].Task_Object.Object_Code == 'Branch') {
                            autoCompletedBranchTaskID = nextTask[i].Task_ID;
                            nextStepFound = true;
                        }
                        continue;
                    }
                    if (
                        nextTask[i].Workflow_Step &&
                        nextTask[i].Workflow_Step.Auto_Run_Ind == 'Y' &&
                        nextTask[i].Assigned_To &&
                        nextTask[i].Assigned_To.Entity_ID == this.app.UserID
                    ) {
                        this.addTaskToQueue(nextTask[i]);
                        nextStepFound = true;
                    }
                }
                if (nextStepFound) {
                    if (autoCompletedBranchTaskID) {
                        // if the next step was an autocompleted branch -- then load that task and autoprocess next steps
                        this.loadTask(autoCompletedBranchTaskID, true);
                    } else {
                        this.openNextTaskInQueue();
                    }
                    return true;
                }
            }
            // If task is part of a workflow and there are no autorun next steps the navigate to Project Tracker
            if (this.app.dsTaskGet.objectNode.Workflow && this.app.dsTaskGet.objectNode.Workflow.Workflow_Code) {
                this.openNextTaskInQueue(); // if no tasks in queue then project tracker is opened
                return true;
            }
            // otherwise this is an adhoc task -- navigate back to previous page (caller)
            //TODO:  We need to refine this logic
            if (this.isProjectActive) {
                this.openProjectTracker();
            } else {
                this.location.back();
            }

            return true;
        }
        return false;
    }

    load(data: any) {
        // TODO:  should actual data retrieve happen inside of this object?
        this.projectID = data.ConceptOneReply.TaskGet.Task.Project.Project_ID;
        if (this.projectOS.currentProjectID != this.projectID) {
            this.projectOS.loadObject(this.projectID);
        }

        if (data.ConceptOneReply.TaskGet.Task.Unread_Message_Ind == 'Y') {
            this.taskService.markRead(data.ConceptOneReply.TaskGet.Task.Task_ID);
            //Manually set to N in the datasource, since there is not a re-retrieve after TaskService.markRead()
            data.ConceptOneReply.TaskGet.Task.Unread_Message_Ind = 'N';
        }
        let normalizedData = normalize(data, rootSchema);
        this.store.dispatch(new SetTaskObjectState(normalizedData.entities));
    }

    async autoSaveTask() {
        return new Promise(async (resolve) => {
            let dirty = this.store.selectSnapshot(PageState.dirty);
            if (!this.taskID || this.taskID == '-1' || dirty) {
                this.autoSaveSub = this.app.dsTaskProcess.status$.subscribe((x) => {
                    switch (x) {
                        case DataSourceStatus.Ready:
                            if (this.autoSaveSub) {
                                this.autoSaveSub.unsubscribe();
                            }
                            this.taskID = this.app.dsTaskProcess.tranNode.Task_ID;
                            resolve(true);
                            break;

                        case DataSourceStatus.Error:
                            if (this.autoSaveSub) {
                                this.autoSaveSub.unsubscribe();
                            }
                            resolve(false);
                            break;
                    }
                });

                this.process(EventType.Update, {}, true);
            } else {
                resolve(true);
            }
        });
    }

    setPageID(pageID: any) {
        this.pageID = pageID;
    }

    resetTask() {
        this.taskID = null;
        this.store.dispatch(new ResetTask());
    }

    resetState() {
        this.taskID = null;
        this.projectID = null;
        this.currentTrackerView = null;
        this.pageID = null;
        this.store.dispatch(new ResetState());
        this.projectOS.resetState();
    }

    updateTask(task: any) {
        this.store.dispatch(new UpdateTask(task));
    }

    cloneTask() {
        this.taskID = null;
        this.store
            .dispatch(new CloneTask())
            .toPromise()
            .then((result) => {
                this.cloneInProgress = true;
                this.process(EventType.Create);
            });
    }

    updateProject(project: any) {
        this.store.dispatch(new UpdateProject(project));
    }

    suppressTaskHeader(on: boolean) {
        this.store.dispatch(new SuppressTaskHeader(on));
    }

    setCurrentReference(task: any) {
        this.store.dispatch(new SetCurrentReference(task));
    }

    assignToMe() {
        let task = this.store.selectSnapshot(TaskObjectState.task);
        task = cloneDeep(task);
        let route = { Assign_To: { User_Entity_ID: this.app.UserID } };
        task.Route = route;

        this.store.dispatch(new UpdateTask(task)).subscribe((x) => {
            this.process(EventType.Update);
        });
    }

    updateRefInfo(task: any, refData: any, refType: any) {
        this.store.dispatch(new SetTaskReference(task, refData, refType));
    }

    setProjectState(project: any) {
        //this should only be used by projectTracker!!!
        this.store.dispatch(new SetProjectState(project));
    }

    updateTaskHeader(taskHeader: TaskHeaderType) {
        //this should only be used by projectTracker!!!
        this.store.dispatch(new UpdateTaskHeader(taskHeader));
    }

    setCurrentTaskHeaderLayout(taskHeaderLayout: TaskHeaderLayoutType) {
        //this should only be used by projectTracker!!!
        this.store.dispatch(new SetCurrentTaskHeaderLayout(taskHeaderLayout));
    }

    addTaskToQueue(task: any) {
        this.store.dispatch(new AddTaskToQueue(task));
    }

    removeTaskFromQueue(taskID: number) {
        this.store.dispatch(new RemoveTaskFromQueue(taskID));
    }

    notifyDocumentSaved() {
        this.store.dispatch(new NotifyDocumentSaved());
    }

    get documentUpdateTimestamp(): any {
        return this.store.selectSnapshot(TaskObjectState.documentUpdateTimestamp);
    }

    setCurrentReferenceContract(contract: any) {
        this.store.dispatch(new SetCurrentReferenceContract(contract));
    }

    validate(event: PageEventType) {
        let stateEntities = this.store.selectSnapshot(TaskObjectState.entities);
        let evaluatedStateObjects = this.objectRuleService.validateState(stateEntities, event, this);
        return evaluatedStateObjects.length == 0;
    }

    setValidEvents(newObject: boolean = false) {
        let validEvents: PageEventType[] = [];
        let saveValidationEvent: PageEventType;
        let anticipatedValidationEvent: PageEventType;

        if (newObject) {
            validEvents.push(PageEventType.Delete);
            saveValidationEvent = PageEventType.Create;
            anticipatedValidationEvent = PageEventType.Create;
        } else {
            if (this.app.dsTaskGet.objectNode.Resolved_Date && this.app.dsTaskGet.objectNode.Resolved_Date != '') {
                //No Valid Events
            } else {
                if (
                    this.app.dsTaskGet &&
                    this.app.dsTaskGet.objectNode.Started_By &&
                    this.app.dsTaskGet.objectNode.Started_By.Date
                ) {
                    //Don't allow Delete once Task is started.
                    saveValidationEvent = PageEventType.Create;
                    anticipatedValidationEvent = PageEventType.Complete;
                } else {
                    validEvents.push(PageEventType.Delete);
                    saveValidationEvent = PageEventType.Create;
                    anticipatedValidationEvent = PageEventType.Create;
                }
            }
        }

        this.pageOS.setValidEvents(validEvents, saveValidationEvent, anticipatedValidationEvent);
    }

    getMissingEvents(): any[] {
        return this.pageOS.getMissingEvents();
    }
}
